import { createContext, useContext, useLayoutEffect } from 'react';
import { ProjectType } from '../../../api/project/types';
import { BatteryType, EnergyType } from '../../../api/properties/types';
import { DeviceKey, Devices, DeviceStatus, DeviceZoom } from '../../../components/RiveHome/RiveHomeView';
import { EnergyFlowStatus } from '../../../hooks/useEnergyFlowStatus';

export type HomeHeroScene = { devices: Devices; arePowerLinesVisible: boolean; deviceZoom?: DeviceZoom };

export const BATTERY_DATA_SOURCES = [EnergyType.storage, BatteryType.acb, BatteryType.encharge];

const IDLE: DeviceStatus = { installInProgress: false };
const INSTALL_IN_PROGRESS: DeviceStatus = { installInProgress: true };
const INSTALLED_DEVICE_BY_PROJECT_TYPE: Partial<Record<ProjectType, DeviceKey>> = {
  hvac: 'hvac',
  waterHeater: 'waterHeater',
  geothermal: 'geothermal',
  generator: 'generator',
};

export const getHomeHeroDevices = (
  flowStatus: EnergyFlowStatus,
  {
    hasProductionDataSource,
    hasConsumptionDataSource,
    hasBatteryDataSource,
    isInstallingPanels,
    isInstallingBattery,
    inProgressProjectTypes = [],
  }: {
    hasProductionDataSource?: boolean;
    hasConsumptionDataSource?: boolean;
    hasBatteryDataSource?: boolean;
    isInstallingPanels?: boolean;
    isInstallingBattery?: boolean;
    inProgressProjectTypes?: ProjectType[];
  },
): Devices => ({
  ...Object.fromEntries(
    inProgressProjectTypes
      .map((projectType) => INSTALLED_DEVICE_BY_PROJECT_TYPE[projectType])
      .filter((device): device is DeviceKey => device !== undefined)
      .map((device) => [device, INSTALL_IN_PROGRESS]),
  ),
  ...(isInstallingPanels && { solar: INSTALL_IN_PROGRESS }),
  ...(hasProductionDataSource && {
    solar:
      flowStatus.isConsumingFromSolar || flowStatus.isChargingBatteryFromSolar || flowStatus.isExportingToGrid
        ? { powerFlow: 'out' }
        : IDLE,
  }),
  ...(isInstallingBattery && { battery: INSTALL_IN_PROGRESS }),
  ...(hasBatteryDataSource && {
    battery: flowStatus.isCharging ? { powerFlow: 'in' } : flowStatus.isDischarging ? { powerFlow: 'out' } : IDLE,
  }),
  ...(hasConsumptionDataSource && {
    grid: flowStatus.isImportingFromGrid
      ? { powerFlow: 'out' }
      : flowStatus.isExportingToGrid || flowStatus.isBatteryExportingToGrid
      ? { powerFlow: 'in' }
      : IDLE,
    inverter: flowStatus.isConsuming ? { powerFlow: 'out' } : IDLE,
  }),
});

export const homeHeroSceneContext = createContext<(scene: HomeHeroScene | undefined) => void>(() => undefined);

export const useHomeHeroScene = (scene: HomeHeroScene) => {
  const setHomeHeroScene = useContext(homeHeroSceneContext);

  useLayoutEffect(() => {
    setHomeHeroScene(scene);
    return () => {
      setHomeHeroScene(undefined);
    };
  }, [scene, setHomeHeroScene]);
};
